/**
 * This file contains the logic for creating an idb connection and reading from it.
 *
 * Thew write path is intentionally split out as only the read path is needed for early
 * requesters.
 */

import { getGlobal, setGlobal } from './global';

// Disables the indexeddb write behavior to make testing less painful.
let __disableIdb = false;

/**
 * Re-enables indexeddb for testing this file
 */
export const __TEST_ONLY__REENABLE_IDB = () => {
  __disableIdb = false;
};

/**
 * Disables the IndexedDB integration. Only for use in tests!
 */
export const __TEST_ONLY__DISABLE_IDB = () => {
  __disableIdb = true;
};
export const CACHE_OBJECT_STORE = 'cache';
export const METRICS_OBJECT_STORE = 'metrics';
const open = ({
  resolvePortalAndUserId
}) => resolvePortalAndUserId().then(({
  portalId,
  userId
}) => new Promise((resolve, reject) => {
  try {
    const openRequest = window.indexedDB.open(`persist-promise/cache/${portalId}/${userId}`, 1);
    if (openRequest) {
      openRequest.onerror = reject;
      openRequest.onupgradeneeded = () => {
        const result = openRequest.result;
        if (!result.objectStoreNames.contains(CACHE_OBJECT_STORE)) {
          result.createObjectStore(CACHE_OBJECT_STORE);
        }
        if (!result.objectStoreNames.contains(METRICS_OBJECT_STORE)) {
          result.createObjectStore(METRICS_OBJECT_STORE);
        }
      };
      openRequest.onsuccess = () => {
        // Set a global error handler on the db
        openRequest.result.onerror = console.error;
        resolve(openRequest.result);
      };
    }
  } catch (e) {
    reject(e);
  }
}));
const idbConnectionKey = '__persistPromiseIDBConnection';
export const openIndexedDBConnection = ({
  resolvePortalAndUserId
}) => {
  if (__disableIdb) {
    return Promise.reject(Error('IndexedDB is disabled'));
  }
  const existingConn = getGlobal(idbConnectionKey);
  if (existingConn) {
    return existingConn;
  }
  const conn = open({
    resolvePortalAndUserId
  });
  setGlobal(idbConnectionKey, conn);
  return conn;
};

/**
 *
 * @param key The key to query in indexeddb
 * @returns A promise that resolves to a value, or rejects if the value is null/undefined or some other error ocurred
 */
export const getFromIDB = ({
  key,
  store = CACHE_OBJECT_STORE,
  resolvePortalAndUserId
}) => openIndexedDBConnection({
  resolvePortalAndUserId
}).then(db => new Promise((resolve, reject) => {
  try {
    const request = db.transaction(store, 'readonly').objectStore(store).get(key);
    request.onerror = reject;
    request.onsuccess = () =>
    // NOTE: Load-bearing != (not !==)
    request.result != null ? resolve(request.result) : reject(Error('Result was empty'));
  } catch (e) {
    reject(e);
  }
}));